import * as React from 'react';
import { useReferralPageContext } from 'contexts/referral';
import { DateTime } from 'luxon';
import View from 'components/View';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Heading from 'components/text/Heading';
import { LUXON_MIDNIGHT } from 'utils/date';
import { useGetIsReferralCampaignActive } from './hooks/useGetIsReferralCampaignActive';
import { useGetIsReferralCampaignNearlyExpired } from './hooks/useGetIsReferralCampaginNearlyExpired';
import { useAuth } from 'contexts/authUser/store';
import { authUserSelectors } from 'contexts/authUser';
import analytics, { ANALYTIC_EVENTS } from 'utils/firebase/analytics';
import { useRouter } from 'next/router';

const FeatureSection = dynamic(() => import('containers/PoinzCards/FeatureSection'));
const HeaderSection = dynamic(() => import('components/HeaderSection'));
const CTAButton = dynamic(() => import('./CTAButton'));
const FaqSection = dynamic(() => import('containers/PoinzCards/FaqSection'));
const FirstStepsSection = dynamic(() => import('containers/PoinzCards/FirstStepsSection'));
const ReferralCampainExpiredDialog = dynamic(() => import('./ReferralCampainExpiredDialog'));

const ReferralContainer: React.FC = () => {
  const { t } = useTranslation();
  const { isLoggedIn, user } = useAuth(state => ({
    isLoggedIn: authUserSelectors.isLoggedIn(state),
    user: state.user
  }));
  const { referralCampaign } = useReferralPageContext();
  const { recipientName: name, endDate: rawEndDate, terms } = referralCampaign || {};
  const { query } = useRouter();
  const endDate = DateTime.fromISO(rawEndDate as any).set(LUXON_MIDNIGHT);
  const dateDiff = endDate.diffNow('days').toObject();
  const isActive = useGetIsReferralCampaignActive();
  const nearlyExpired = useGetIsReferralCampaignNearlyExpired();

  const getExpiryLabel = () => {
    if (isActive && nearlyExpired) {
      return t('general.onlyDaysLeft', {
        count: Math.floor(Number(dateDiff.days))
      });
    }
    if (!isActive) {
      return t('referral.thisCampaignHasExpired');
    }
    return;
  };

  React.useEffect(() => {
    if (isLoggedIn && user && query.code) {
      analytics.logEvent(ANALYTIC_EVENTS.REFERRAL_PAGE_OPEN, {
        id: user.id,
        promoCode: query.code,
        type: 'WEB'
      });
    }
  }, [isLoggedIn, query, user]);

  return (
    <div className="Referral">
      <HeaderSection
        title={name as string}
        label={getExpiryLabel()}
        description={t('referral.swissLoyaltyCards.subtitle') as string}
        ctaButton={<CTAButton terms={terms} />}
      />
      <View className="pt-20 md:pt-0 gap-4 flex flex-col flex-1">
        <Heading variant="h2" className="mb-2 md:w-[50%] text-center self-center">
          {t('referral.featuresTitle')}
        </Heading>
        <FeatureSection />
      </View>
      <FirstStepsSection className="pt-20" />
      <View className="pt-20">
        <FaqSection />
      </View>
      <ReferralCampainExpiredDialog />
    </div>
  );
};

export default ReferralContainer;
